// Нижняя шторка — общий примитив телефонной оболочки (M1.5).
//
// На десктопе агент, контекст и диктовка живут в правой колонке. На телефоне
// правых колонок нет: всё это поднимается снизу шторкой. Примитив один на все
// случаи, чтобы скругление, затемнение и safe-area не расходились от экрана
// к экрану.
//
// Закрытая шторка не рендерит содержимое вовсе, а не прячет его классом:
// внутри живут диалог агента и запись с микрофона, и держать их смонтированными
// «на всякий случай» — значит держать включённым микрофон.

import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  open: boolean
  title?: string
  onClose(): void
  children?: ReactNode
}

/** Открытые слои снизу вверх: последний — верхний, он и ловит Escape. */
const layers: number[] = []
let nextLayerId = 0

/**
 * Escape для одного слоя оболочки (шторка, диктовка).
 *
 * Обработчик у каждого слоя свой и висит на window, поэтому одно нажатие
 * приходило во все открытые слои сразу: диктовка живёт внутри шторки агента, и
 * Escape закрывал обе. Общий стек слоёв решает это: реагирует только тот, кто
 * сверху, остальные нажатие игнорируют. Слои с разными обработчиками (диктовке
 * нужно ещё и остановить запись) поэтому и не сведены в один компонент.
 */
export function useEscapeLayer(open: boolean, onEscape: () => void): void {
  // Колбэк держим в ref: иначе смена его identity пересобирала бы эффект и
  // слой всплывал бы в стеке на ровном месте.
  const escapeRef = useRef(onEscape)
  escapeRef.current = onEscape

  useEffect(() => {
    if (!open) return
    const id = nextLayerId++
    layers.push(id)
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (layers[layers.length - 1] !== id) return // не верхний слой — не наше нажатие
      escapeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      const at = layers.lastIndexOf(id)
      if (at >= 0) layers.splice(at, 1)
    }
  }, [open])
}

export default function Sheet({ open, title, onClose, children }: Props) {
  // Esc закрывает шторку: на телефоне это клавиатура с внешнего чехла,
  // на отладке в браузере — единственный способ выйти без мыши.
  useEscapeLayer(open, onClose)

  if (!open) return null

  // Шторку рендерим порталом в корень оболочки, а не там, где её позвали.
  // Причина простая: зовут её из середины экрана, у которого своя прокрутка
  // и, скорее всего, свой position: relative — и шторка оказалась бы обрезана
  // телом экрана вместо того, чтобы накрыть его целиком вместе с таб-баром.
  //
  // Именно в корень оболочки, а не в document.body: корень высотой с видимую
  // часть экрана (--app-h), поэтому с открытой клавиатурой шторка поднимается
  // вместе с ней, а не остаётся под ней, как это делает position: fixed.
  const host = document.querySelector('.ab-mobile') ?? document.body

  return createPortal(
    <div className="ab-sheet" role="dialog" aria-modal="true" aria-label={title}>
      {/* Затемнение закрывает шторку тапом — на телефоне это привычнее крестика. */}
      <div className="ab-sheet__scrim" onClick={onClose} />
      <div className="ab-sheet__panel">
        <div className="ab-sheet__grip" aria-hidden />
        {title ? (
          <header className="ab-sheet__head">
            <span className="ab-sheet__title">{title}</span>
            <button className="ab-mbtn" onClick={onClose} aria-label="закрыть" title="закрыть">
              ×
            </button>
          </header>
        ) : null}
        <div className={`ab-sheet__body${title ? '' : ' ab-sheet__body--bare'}`}>{children}</div>
      </div>
    </div>,
    host,
  )
}
